import { MemoPreviewField } from 'components/general/PreviewField';
import { hasGeneralNormContent } from 'components/inspection/GeneralNormPreview/model';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys, InspectionTemplateTranslationKeys } from 'intl/keys';
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { InspectionFormPresentationalProps } from '../types';
import {
  DEFAULT_EXPAND_ALL_CHECKLIST,
  resolveChecklistItemIdAsIs,
} from '../model';
import { InspectionProtocolVirtualList } from '../InspectionProtocolVirtualList';
import { useInspectionDocumentDeepLinkExpansion } from '../../useInspectionDocumentDeepLinkExpansion';
import { Box, Divider, Stack } from '@mui/material';

import { MemoProcessIcon } from 'components/common/ProcessIcon';
import { InspectionScoreLive } from 'components/inspection/InspectionScore';
import {
  MemoStatefulDatepicker,
  MemoStatefulMultiselect,
  MemoStatefulRichTextField,
} from 'components/dataCreationForms/withInputForm/statefulInputsWrapper';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import { ContextedGeneralNormPreview } from '../../GeneralNormPreview';
import { MemoInspectionStatus } from '../../Status';
import { InspectionStatusSize } from '../../Status/types';
import { InspectionStatusDropdown } from '../../StatusDropdown';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { SubheaderText, actionToolbarSx } from 'components/general';
import { SubmitFooter } from 'components/submitFooter';
import { useAccordionsExpansion } from 'hooks/useAccordionsExpansion';
import {
  inspectionGridFullWidthItemSx,
  inspectionGridSx,
  inspectionSectionPaddingSx,
  inspectionSubheaderBaseSx,
} from 'components/inspection/layout';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { isProtocolAnswerFilled } from 'shared/domain/protocolAnswer';
import {
  buildChecklistTreeFromSources,
  isSectionNode,
  TreeNode,
} from 'shared/domain/checklistTree';
import { toEntityMap } from 'helpers/mappings';
import { CollapsibleInspectionComment } from 'components/inspection/CollapsibleInspectionComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import { computeSectionApplicabilityById } from 'shared/domain/inspection/mapping/isSectionAnswersFullyNotApplicable';
import { useInspection, useInspectionFieldDisabled, useInspectionTemplate } from 'store/inspection';
import { checklistItemIdsCoveredByNotApplicableSections, hasMissingRequiredFinishProtocolAnswers, shouldReopenInspectionAfterRequiredAnswersCleared } from 'views/inspections/wizard/validation';
import { isFieldDisabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/model';
import { Disabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/types';
import { InspectionProtocolLookup } from 'components/inspection/protocolLookup';

export function InspectionFormPresentational({
  isSaving = false,
  isNewInspection,
  inspectionCode,
  template,
  executionFieldProps,
  workTypesFieldProps,
  sitesFieldProps,
  levelsFieldProps,
  controlledPartiesFieldProps,
  contractsFieldProps,
  commentFieldProps,
  checklist,
  sections,
  protocolIssues,
  onSubmit,
  onCancel,
  atomicMode = false,
  editPermission,
  actionButtons,
  inspectionStatus = InspectionStatus.inProgress,
  onStatusChange,
  isArchived,
}: InspectionFormPresentationalProps): ReactElement {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { values } = useInputForm();
  const { inspectionModel } = useInspection();
  const { inspectionTemplateDocuments } = useInspectionTemplate();
  const hasGeneralNormData = hasGeneralNormContent(
    template,
    inspectionTemplateDocuments
  );
  const resolveFieldDisabled = useInspectionFieldDisabled();

  const permissionStatusDisabled = useMemo(
    () => resolveFieldDisabled(false),
    [resolveFieldDisabled]
  );
  const [sectionSkippedOverride, setSectionSkippedOverride] = useState<
    Record<string, boolean>
  >({});

  const checklistById = useMemo(() => {
    return toEntityMap(checklist, (item) => item._id);
  }, [checklist]);

  const sectionCommentById = useMemo(() => {
    return new Map(
      sections.map((section) => [
        section._id,
        section.inspectionDescription || '',
      ])
    );
  }, [sections]);

  const checklistTree = useMemo(() => {
    return buildChecklistTreeFromSources({
      sections,
      items: checklist,
    });
  }, [sections, checklist]);

  const expandableNodeIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...sections.map((section) => section._id),
          ...checklist.map((item) => item._id),
        ])
      ),
    [sections, checklist]
  );

  const {
    expandedIds,
    allExpanded,
    setAllExpanded,
    toggleExpandAll,
    onItemExpandedChange,
    isExpandingAll,
  } = useAccordionsExpansion(expandableNodeIds, {
    defaultExpandAll: DEFAULT_EXPAND_ALL_CHECKLIST,
    resolveReturnChecklistItemId: resolveChecklistItemIdAsIs,
  });

  useInspectionDocumentDeepLinkExpansion(onItemExpandedChange);

  const hasExpanded = expandedIds.size > 0;

  const handleToggleExpandAll = useCallback(() => {
    if (hasExpanded) {
      setAllExpanded(false);
      return;
    }

    toggleExpandAll();
  }, [hasExpanded, setAllExpanded, toggleExpandAll]);

  interface SectionAnswerStats {
    answeredItems: number;
    totalItems: number;
  }

  const isAnswerFilledInForm = useCallback(
    (checklistItemId: string, answerId: string, answerType: AnswerType): boolean => {
      return isProtocolAnswerFilled(values, checklistItemId, answerId, answerType);
    },
    [values]
  );

  const sectionStatsById = useMemo(() => {
    const statsById = new Map<string, SectionAnswerStats>();

    const countNode = (node: TreeNode): SectionAnswerStats => {
      if (!isSectionNode(node)) {
        const checklistItem = checklistById.get(node.id);
        const answerConfigs = checklistItem?.answers ?? [];
        const filledCount = answerConfigs.filter((answer) =>
          isAnswerFilledInForm(node.id, answer._id, answer.type)
        ).length;
        const hasAnyFilled = filledCount > 0 || answerConfigs.length === 0;

        return {
          answeredItems: hasAnyFilled ? 1 : 0,
          totalItems: 1,
        };
      }

      const stats = node.children.reduce(
        (acc, child) => {
          const childStats = countNode(child);

          return {
            answeredItems: acc.answeredItems + childStats.answeredItems,
            totalItems: acc.totalItems + childStats.totalItems,
          };
        },
        { answeredItems: 0, totalItems: 0 }
      );

      statsById.set(node.id, stats);

      return stats;
    };

    checklistTree.forEach(countNode);

    return statsById;
  }, [checklistTree, checklistById, isAnswerFilledInForm]);

  const sectionApplicabilityById = useMemo(() => {
    const sectionKeyIds: string[] = [];
    const collectSectionIds = (node: TreeNode): void => {
      if (!isSectionNode(node)) {
        return;
      }
      sectionKeyIds.push(node.id);
      node.children.forEach(collectSectionIds);
    };
    checklistTree.forEach(collectSectionIds);

    return computeSectionApplicabilityById({
      inspectionModel,
      sectionKeyIds,
      checklistItems: checklist,
      checklistSections: sections,
    });
  }, [checklistTree, inspectionModel, checklist, sections]);

  const notApplicableChecklistItemIds = useMemo(() => {
    const notApplicableSectionIds = new Set<string>();
    sectionApplicabilityById.forEach((applicability, sectionId) => {
      const override = sectionSkippedOverride[sectionId];
      const skipped = override ?? applicability.isNotApplicable;
      if (skipped) {
        notApplicableSectionIds.add(sectionId);
      }
    });
    Object.entries(sectionSkippedOverride).forEach(([sectionId, skipped]) => {
      if (skipped) {
        notApplicableSectionIds.add(sectionId);
      }
    });

    return checklistItemIdsCoveredByNotApplicableSections(
      sections,
      checklist,
      notApplicableSectionIds
    );
  }, [checklist, sectionApplicabilityById, sectionSkippedOverride, sections]);

  const missingRequiredAnswers = useMemo(
    () =>
      hasMissingRequiredFinishProtocolAnswers(
        template,
        values,
        inspectionModel?.protocol,
        notApplicableChecklistItemIds
      ),
    [
      inspectionModel?.protocol,
      notApplicableChecklistItemIds,
      template,
      values,
    ]
  );
  const answersBlockStatus =
    inspectionStatus === InspectionStatus.inProgress &&
    missingRequiredAnswers &&
    !isFieldDisabled(permissionStatusDisabled);
  const statusFieldDisabled: Disabled = answersBlockStatus
    ? true
    : permissionStatusDisabled;
  const statusHelperText = answersBlockStatus
    ? formatTranslation(intl, InspectionTranslationKeys.answersNotFilledError)
    : undefined;
  const hadMissingRequiredAnswersRef = useRef(missingRequiredAnswers);

  const handleSectionSkippedChange = useCallback(
    (sectionId: string, skipped: boolean) => {
      setSectionSkippedOverride((current) => ({
        ...current,
        [sectionId]: skipped,
      }));
    },
    []
  );

  // Drops the user's optimistic skip overrides once the saved applicability matches them.
  useEffect(() => {
    setSectionSkippedOverride((current) => {
      let changed = false;
      const next = { ...current };
      Object.entries(current).forEach(([sectionId, skipped]) => {
        const modelSkipped =
          sectionApplicabilityById.get(sectionId)?.isNotApplicable ?? false;
        if (modelSkipped === skipped) {
          delete next[sectionId];
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [sectionApplicabilityById]);

  useEffect(() => {
    const hadMissingRequiredAnswers = hadMissingRequiredAnswersRef.current;
    hadMissingRequiredAnswersRef.current = missingRequiredAnswers;

    if (
      !onStatusChange ||
      !shouldReopenInspectionAfterRequiredAnswersCleared(
        inspectionStatus,
        hadMissingRequiredAnswers,
        missingRequiredAnswers
      )
    ) {
      return;
    }

    onStatusChange(InspectionStatus.inProgress);
  }, [inspectionStatus, missingRequiredAnswers, onStatusChange]);

  return (
    <InspectionProtocolLookup>
      <>
      <ViewWrapper>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{
            ...inspectionSectionPaddingSx,
            mt: 4,
            mb: 4,
            flexWrap: 'nowrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <MemoProcessIcon
              code={template.renderableProcess.code}
              color={template.renderableProcess.color}
            />
            <SubheaderText
              sx={{
                ...inspectionSubheaderBaseSx,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              {isNewInspection
                ? formatTranslation(intl, InspectionTranslationKeys.newTitle)
                : inspectionCode}
            </SubheaderText>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isArchived && (
              <Box
                component='span'
                sx={{ color: 'error.main', fontSize: '0.875rem' }}
              >
                {formatTranslation(
                  intl,
                  InspectionTranslationKeys.previewInspectionRemoved
                )}
              </Box>
            )}
            {atomicMode && actionButtons ? (
              <Box sx={actionToolbarSx}>{actionButtons}</Box>
            ) : null}
          </Box>
        </Stack>

        <Divider />

        <SubheaderText
          sx={{
            ...inspectionSubheaderBaseSx,
            mt: 4,
            mb: 4,
          }}
        >
          {template.summary}
        </SubheaderText>

        <Box sx={inspectionGridSx}>
          <Box>
            <MemoStatefulDatepicker {...executionFieldProps} />
          </Box>
          <Box>
            {onStatusChange ? (
              <InspectionStatusDropdown
                value={inspectionStatus}
                onChange={onStatusChange}
                disabled={statusFieldDisabled}
                helperText={statusHelperText}
              />
            ) : (
              <MemoPreviewField
                label={formatTranslation(intl, InspectionTranslationKeys.status)}
                component={
                  <MemoInspectionStatus
                    status={InspectionStatus.inProgress}
                    size={InspectionStatusSize.default}
                  />
                }
                dense={true}
              />
            )}
          </Box>

          <Box>
            <MemoStatefulMultiselect {...sitesFieldProps} />
          </Box>
          <Box>
            <MemoStatefulMultiselect {...levelsFieldProps} />
          </Box>
          {template.generalNormConfig?.isControlledPartyVisible !== false && (
            <Box>
              <MemoStatefulMultiselect {...controlledPartiesFieldProps} />
            </Box>
          )}
          {template.generalNormConfig?.isContractNumberVisible !== false && (
            <Box>
              <MemoStatefulMultiselect {...contractsFieldProps} />
            </Box>
          )}
          {template.generalNormConfig?.isWorkTypeVisible !== false && (
            <Box>
              <MemoStatefulMultiselect {...workTypesFieldProps} />
            </Box>
          )}
          {template.generalNormConfig?.isInspectionResultVisible !== false && (
            <Box>
              <InspectionScoreLive />
            </Box>
          )}
          {template.generalNormConfig?.isCommentVisible !== false && (
            <Box sx={inspectionGridFullWidthItemSx}>
              <CollapsibleInspectionComment
                formKey={commentFieldProps.formKey}
                disabled={commentFieldProps.disabled}
                labelVariant={InspectionCommentLabelVariant.floating}
              >
                <MemoStatefulRichTextField {...commentFieldProps} />
              </CollapsibleInspectionComment>
            </Box>
          )}
        </Box>
        {hasGeneralNormData && (
          <>
            <Divider sx={{ my: 4 }} />
            <SubheaderText
              sx={{
                ...inspectionSubheaderBaseSx,
                margin: '16px 0',
              }}
            >
              {formatTranslation(
                intl,
                InspectionTemplateTranslationKeys.headerGeneralAppliedStandard
              )}
            </SubheaderText>
            <Box sx={inspectionGridSx}>
              <Box sx={inspectionGridFullWidthItemSx}>
                <ContextedGeneralNormPreview />
              </Box>
            </Box>
          </>
        )}
        <Divider sx={{ my: 4 }} />
        <InspectionProtocolVirtualList
          checklistTree={checklistTree}
          checklistById={checklistById}
          expandedIds={expandedIds}
          allExpanded={allExpanded}
          hasExpanded={hasExpanded}
          isExpandingAll={isExpandingAll}
          pathname={pathname}
          protocolIssues={protocolIssues}
          sectionCommentById={sectionCommentById}
          sectionStatsById={sectionStatsById}
          sectionApplicabilityById={sectionApplicabilityById}
          onToggleExpandAll={handleToggleExpandAll}
          onItemExpandedChange={onItemExpandedChange}
          onSectionSkippedChange={handleSectionSkippedChange}
        />
      </ViewWrapper>
      {!atomicMode && onSubmit && onCancel ? (
        <SubmitFooter
          isPosting={isSaving}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      ) : null}
      </>
    </InspectionProtocolLookup>
  );
}
